import { FC, useEffect, useState, useCallback } from 'react';
import { css } from '@emotion/react';
import { useHistory, useParams } from 'react-router-dom';
import { formatDistanceToNowStrict } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Capacitor } from '@capacitor/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { colors, spacing, fontStyles } from '../../styles';
import Page from '../Page';
import Button from '../Button';
// import * as stores from '../../stores';
import {
  SmartNotificationSummary,
  getSmartSummaryById,
  markSmartSummaryAsRead,
  MarketIndicator,
} from '../../apis/backend';
import { PushNotificationService } from '../../services/pushNotificationService';

const SummaryHeader: FC<{
  title: string;
}> = ({ title }) => {
  const history = useHistory();

  const handleGoBack = () => {
    history.goBack();
  };

  return (
    <>
      {/* Header with back button */}
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: ${spacing.milli};
          margin-bottom: ${spacing.centi};
          padding: ${spacing.milli} 0;
        `}
      >
        <button
          onClick={handleGoBack}
          css={css`
            background: none;
            border: none;
            padding: ${spacing.micro};
            cursor: pointer;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: background-color 0.2s ease;

            &:hover {
              background-color: ${colors.cloud};
            }

            &:active {
              background-color: ${colors.fog};
            }

            html[data-theme='dark'] & {
              &:hover {
                background-color: ${colors.liquorice};
              }

              &:active {
                background-color: ${colors.tungsten};
              }
            }
          `}
        >
          <span
            css={css`
              font-size: 20px;
              color: ${colors.liquorice};
              html[data-theme='dark'] & {
                color: ${colors.sugar};
              }
            `}
          >
            ←
          </span>
        </button>
        <h1
          css={css`
            color: ${colors.liquorice};
            margin: 0;
            ${fontStyles.shark}
            html[data-theme='dark'] & {
              color: ${colors.sugar};
            }
          `}
        >
          {title}
        </h1>
      </div>
    </>
  );
};

// Market Indicators Component (same as Markets page)
const MarketIndicators: FC<{
  indicators?: {
    aaii?: MarketIndicator;
    fearIndex?: MarketIndicator;
    fear_index?: MarketIndicator;
    volatility?: MarketIndicator;
  };
}> = ({ indicators }) => {
  if (!indicators || Object.keys(indicators).length === 0) {
    return null;
  }

  const getColorForSentiment = (sentiment: string) => {
    if (sentiment === 'positive') return colors.leaf;
    if (sentiment === 'negative') return colors.berry;
    return colors.silver;
  };

  // Support both fearIndex (camelCase) and fear_index (snake_case) from backend
  const fearIndexValue = indicators.fearIndex || indicators.fear_index;

  const indicatorData = [
    { key: 'aaii', label: 'AAII', value: indicators.aaii },
    { key: 'fearIndex', label: 'Fear Index', value: fearIndexValue },
    { key: 'volatility', label: 'Volatility', value: indicators.volatility },
  ];

  return (
    <div
      css={css`
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: ${spacing.centi};
        margin-bottom: ${spacing.regular};
      `}
    >
      {indicatorData.map(({ key, label, value }) => {
        if (!value) return null;
        const color = getColorForSentiment(value.sentiment);

        return (
          <div
            key={key}
            css={css`
              background: ${colors.snow};
              border: 1px solid rgba(203, 202, 201, 0.3);
              border-radius: 12px;
              padding: ${spacing.deci};
              text-align: center;
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

              html[data-theme='dark'] & {
                background: #1a1919;
                border-color: rgba(80, 78, 75, 0.3);
              }
            `}
          >
            <div
              css={css`
                ${fontStyles.cat}
                color: ${colors.silver};
                margin-bottom: ${spacing.micro};
                font-weight: 500;
                font-size: 12px;
                text-transform: uppercase;
                letter-spacing: 0.5px;

                html[data-theme='dark'] & {
                  color: ${colors.tungsten};
                }
              `}
            >
              {label}
            </div>
            <div
              css={css`
                ${fontStyles.dog}
                color: ${color};
                font-weight: 600;
              `}
            >
              {value.value}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Summary Card Component (same as Markets page)
const SummaryCard: FC<{
  summary: SmartNotificationSummary;
}> = ({ summary }) => {
  const bulletPoints = summary.bulletPoints || [];

  return (
    <div
      css={css`
        background: ${colors.snow};
        border: 1px solid rgba(203, 202, 201, 0.3);
        border-radius: 16px;
        padding: ${spacing.deci};
        margin-bottom: ${spacing.regular};
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

        html[data-theme='dark'] & {
          background: #1a1919;
          border-color: rgba(80, 78, 75, 0.3);
        }
      `}
    >
      <div
        css={css`
          margin-bottom: ${spacing.centi};
        `}
      >
        <h2
          css={css`
            ${fontStyles.horse}
            color: ${colors.liquorice};
            margin: 0 0 ${spacing.micro} 0;
            html[data-theme='dark'] & {
              color: ${colors.sugar};
            }
          `}
        >
          {summary.title}
        </h2>
        <span
          css={css`
            ${fontStyles.cat}
            color: ${colors.silver};
            font-size: 13px;
            html[data-theme='dark'] & {
              color: ${colors.tungsten};
            }
          `}
        >
          {formatDistanceToNowStrict(new Date(summary.generatedAt), { addSuffix: false })} ago
        </span>
      </div>

      {bulletPoints.length > 0 ? (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${spacing.centi};
          `}
        >
          {bulletPoints.map((bp, index) => {
            const indicator = bp.sentiment === 'positive' ? '🟢' : bp.sentiment === 'negative' ? '🔴' : '⚪';
            return (
              <div
                key={index}
                css={css`
                  display: flex;
                  gap: ${spacing.milli};
                  align-items: flex-start;
                `}
              >
                <span
                  css={css`
                    font-size: 12px;
                    flex-shrink: 0;
                    margin-top: 2px;
                  `}
                >
                  {indicator}
                </span>
                <span
                  css={css`
                    ${fontStyles.dog}
                    color: ${colors.liquorice};
                    line-height: 1.5;
                    html[data-theme='dark'] & {
                      color: ${colors.sugar};
                    }
                  `}
                >
                  {bp.text}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <div
          css={css`
            ${fontStyles.dog}
            color: ${colors.liquorice};
            line-height: 1.6;

            html[data-theme='dark'] & {
              color: ${colors.sugar};
            }

            /* Markdown styles - matching ChatPage for consistency */
            & p {
              margin: 0 0 ${spacing.centi} 0;
              &:last-child {
                margin-bottom: 0;
              }
            }

            & h1 {
              ${fontStyles.lion}
              margin: 0 0 ${spacing.centi} 0;
              font-weight: 600;
            }

            & h2 {
              ${fontStyles.dog}
              margin: 0 0 ${spacing.centi} 0;
              font-weight: 600;
            }

            & h3 {
              ${fontStyles.flea}
              margin: 0 0 ${spacing.milli} 0;
              font-weight: 600;
            }

            & ul,
            & ol {
              margin: ${spacing.centi} 0;
              padding-left: ${spacing.regular};
            }

            & li {
              margin: ${spacing.milli} 0;
            }

            & code {
              background-color: rgba(0, 0, 0, 0.1);
              padding: 2px 4px;
              border-radius: 4px;
              font-size: 0.9em;
            }

            & pre {
              background-color: rgba(0, 0, 0, 0.1);
              padding: ${spacing.deci};
              border-radius: 8px;
              overflow-x: auto;
              margin: ${spacing.centi} 0;
            }

            & strong {
              font-weight: 600;
            }

            & a {
              color: ${colors.petrolBlue};
              text-decoration: underline;

              html[data-theme='dark'] & {
                color: ${colors.skyBlue};
              }
            }

            /* Table styles */
            & .table-wrapper {
              overflow-x: auto;
              -webkit-overflow-scrolling: touch;
              margin: ${spacing.deci} 0;
              border-radius: 8px;
              border: 1px solid ${colors.cloud};
            }

            & table {
              border-collapse: collapse;
              width: 100%;
              display: table;
              font-size: 0.9em;
              margin: 0;
            }

            & th,
            & td {
              border: none;
              border-bottom: 1px solid ${colors.cloud};
              padding: ${spacing.deci};
              text-align: left;
              vertical-align: top;
              white-space: normal;
              word-break: normal;
              line-height: 1.5;
            }

            & th {
              background-color: rgba(0, 0, 0, 0.05);
              font-weight: 600;
              min-width: 120px;
              white-space: nowrap;
              border-bottom: 2px solid ${colors.cloud};
            }

            & td {
              white-space: pre-line;
            }

            & tr:nth-of-type(even) {
              background-color: rgba(0, 0, 0, 0.02);
            }

            & tr:last-child td {
              border-bottom: none;
            }

            html[data-theme='dark'] & {
              & .table-wrapper {
                border-color: ${colors.tungsten};
              }

              & th,
              & td {
                border-color: ${colors.tungsten};
              }

              & th {
                background-color: rgba(255, 255, 255, 0.05);
                border-bottom-color: ${colors.tungsten};
              }

              & tr:nth-of-type(even) {
                background-color: rgba(255, 255, 255, 0.02);
              }

              & code,
              & pre {
                background-color: rgba(255, 255, 255, 0.1);
              }
            }
          `}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw as any]}
            components={{
              table: ({ children, ...props }) => (
                <div className="table-wrapper">
                  <table {...props}>{children}</table>
                </div>
              ),
              td: ({ children, ...props }) => <td {...props}>{children}</td>,
              th: ({ children, ...props }) => <th {...props}>{children}</th>,
              a: ({ href, children, ...props }) => {
                const isInternal = href?.startsWith('/') || href === '#subscribe';
                if (isInternal) {
                  return (
                    <a href={href} {...props}>
                      {children}
                    </a>
                  );
                }
                return (
                  <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                    {children}
                  </a>
                );
              },
            }}
          >
            {summary.content}
          </ReactMarkdown>
        </div>
      )}
    </div>
  );
};

// Chat Button Component (same as Markets page)
const ChatButton: FC<{
  onClick: () => void;
}> = ({ onClick }) => (
  <button
    onClick={onClick}
    css={css`
      background: ${colors.snow};
      border: 1px solid rgba(203, 202, 201, 0.3);
      border-radius: 12px;
      padding: ${spacing.deci};
      ${fontStyles.dog}
      color: ${colors.petrolBlue};
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: ${spacing.milli};
      margin-bottom: ${spacing.regular};

      &:hover {
        background: ${colors.fog};
        transform: translateY(-1px);
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
      }

      &:active {
        transform: translateY(0);
      }

      html[data-theme='dark'] & {
        background: #1a1919;
        border-color: rgba(80, 78, 75, 0.3);
        color: ${colors.skyBlue};

        &:hover {
          background: #242323;
        }
      }
    `}
  >
    Chat about this update
    <span>→</span>
  </button>
);

const SmartSummaryDetailPage: FC = () => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const [summary, setSummary] = useState<SmartNotificationSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadSummary = useCallback(async () => {
    setIsLoading(true);
    try {
      if (!id) {
        setSummary(null);
        return;
      }

      // Try to get device token from PushNotificationService
      let deviceToken = await PushNotificationService.getToken();

      // If no token available, try to get one by initializing the service temporarily
      if (!deviceToken) {
        console.log('No cached token found, attempting to get FCM token...');
        try {
          // Try to get token without full initialization
          const platform = Capacitor.getPlatform();
          if (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('FirebaseMessaging')) {
            const { token } = await FirebaseMessaging.getToken();
            deviceToken = token;
          } else if (platform === 'web') {
            // For web, we need to request permissions first
            const { getMessagingInstance } = await import('../../config/firebase.config');
            const { getToken } = await import('firebase/messaging');
            const { getVapidKey } = await import('../../config/env');

            const messaging = await getMessagingInstance();
            if (!messaging) {
              console.warn('[SmartSummary] Firebase Messaging not available on this platform');
            } else {
              // Request notification permission
              const permission = await Notification.requestPermission();
              if (permission === 'granted') {
                const token = await getToken(messaging, {
                  vapidKey: getVapidKey(),
                });
                deviceToken = token;
              }
            }
          }
        } catch (tokenError) {
          console.warn('Could not obtain FCM token:', tokenError);
        }
      }

      if (!deviceToken) {
        console.warn('No device token available for smart summary detail');
        setSummary(null);
        return;
      }

      // Load summary by ID
      const summary = await getSmartSummaryById(id, deviceToken);
      setSummary(summary);

      // Mark as read if it's not already read
      if (summary && !summary.read) {
        try {
          await markSmartSummaryAsRead(summary.id, deviceToken);
          // Update the summary state to reflect it's now read
          setSummary({ ...summary, read: true });
        } catch (error) {
          console.error('Error marking summary as read:', error);
          // Don't fail the whole operation if marking as read fails
        }
      }
    } catch (error) {
      console.error('Error loading summary detail:', error);
      setSummary(null);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const handleChatClick = () => {
    if (!summary) return;

    // Navigate to chat with pre-populated context (same as Markets page)
    const contextMessage = `Tell me more about this market summary: ${summary.title}`;
    history.push('/chat', { message: contextMessage });
  };

  if (isLoading) {
    return (
      <Page showNavigationBar>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${spacing.centi};
            padding: ${spacing.centi};
          `}
        >
          <div
            css={css`
              height: 200px;
              background: ${colors.cloud};
              border-radius: 16px;
              animation: pulse 1.5s ease-in-out infinite;
              @keyframes pulse {
                0%,
                100% {
                  opacity: 0.6;
                }
                50% {
                  opacity: 1;
                }
              }
            `}
          />
          <div
            css={css`
              height: 300px;
              background: ${colors.cloud};
              border-radius: 16px;
              animation: pulse 1.5s ease-in-out infinite;
            `}
          />
        </div>
      </Page>
    );
  }

  if (!summary) {
    return (
      <Page showNavigationBar>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: ${spacing.centi};
            min-height: 200px;
          `}
        >
          <h2
            css={css`
              color: ${colors.liquorice};
              margin: 0 0 ${spacing.milli} 0;
              ${fontStyles.flea}
              html[data-theme='dark'] & {
                color: ${colors.sugar};
              }
            `}
          >
            Summary not found
          </h2>
          <Button
            onClick={() => history.push('/markets')}
            css={css`
              background: ${colors.petrolBlue};
              color: ${colors.sugar};
              border: none;
              border-radius: 8px;
              padding: ${spacing.milli} ${spacing.centi};
              ${fontStyles.flea}
              cursor: pointer;
            `}
          >
            Back to Summaries
          </Button>
        </div>
      </Page>
    );
  }

  return (
    <Page showNavigationBar>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: ${spacing.centi};
          padding: ${spacing.centi};
        `}
      >
        <SummaryHeader title={summary.title} />

        <MarketIndicators indicators={summary.indicators} />

        <SummaryCard summary={summary} />

        <ChatButton onClick={handleChatClick} />
      </div>
    </Page>
  );
};

export default SmartSummaryDetailPage;
